import { Stack } from '@chakra-ui/react';
import { Vehicle, Workshop } from 'api/types';
import Link from 'components/Link';
import { SelectColumnFilter } from 'components/Table/Filter';
import { useUserContext } from 'context/UserContextProvider';
import useSystemPartnerQuery from 'hooks/useSystemPartnerQuery';
import { ContentCard } from 'layout/ContentCard';
import { isEqual, isNil, keys } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import useServiceHistoriesQuery from '../../hooks/useServiceHistoriesQuery';

const ServiceHistoryOverviewCard = () => {
  const { t } = useTranslation();

  const systemPartenerQuery = useSystemPartnerQuery();
  const systemPartnersData = systemPartenerQuery?.data || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const systemPartners = [
    { name: getI18n().t('workshop:system_partner.no_system_partner'), id: 'none' },
    { name: getI18n().t('workshop:system_partner.with_system_partner'), id: 'all' },
    ...systemPartnersData,
  ];

  const { statusCodes, makes, config } = useUserContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const serviceHistoryCodes = statusCodes?.service_history_request || {};
  const initialStatus = keys(serviceHistoryCodes).filter((code) => !['0'].includes(code));

  const languagesWithDuplicates = Object.values(config?.languages).flat();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dropdownLanguages = [...new Set(languagesWithDuplicates)];

  const dropdownCountries = Object.values(config?.countries);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: initialStatus || [],
    assignee: undefined,
    make: undefined,
    language: undefined,
    systemPartner: undefined,
    country: undefined,
  };

  const localStorageData = window.localStorage.getItem('service_history_requests_filters');
  const destringifiedData = JSON.parse(localStorageData!) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('service_history_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [make, setMake] = useState(destringifiedData?.make || initialFilters?.make);
  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);
  const [systemPartner, setSystemPartner] = useState(destringifiedData?.systemPartner || initialFilters?.systemPartner);
  const [language, setLanguage] = useState(destringifiedData?.language || initialFilters?.language);
  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);
  const [filtersSelected, setFiltersSelected] = useState(false);

  const serviceHistoryQuery = useServiceHistoriesQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    make,
    assignee,
    language,
    systemPartner,
    country,
  });

  // @ts-ignore
  const currentPage = serviceHistoryQuery.data?.meta?.current_page - 1;

  useEffect(() => {
    setStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialLoad =
    searchString === '' &&
    itemsPerPage === 20 &&
    page === 1 &&
    isEqual(initialStatus, status) &&
    isNil(make) &&
    isNil(language) &&
    isNil(assignee) &&
    isNil(country);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, make, status, language, country]);

  useEffect(() => {
    setFiltersSelected(!isNil(assignee) || !isNil(make) || !isNil(language) || status?.length !== 0 || !isNil(country));

    window.localStorage.setItem(
      'service_history_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        itemsPerPage: itemsPerPage,
        language: language,
        make: make,
        systemPartner: systemPartner,
        country: country,
      }),
    );
  }, [searchString, status, assignee, itemsPerPage, systemPartner, make, language, country]);

  const allStatusSelected = status.length === Object.keys(serviceHistoryCodes).length;

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        //@ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'shr-' + row?.index} to={`/app/op/service-history-request/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () =>
          MultiSelectDropDown(status, setStatus, 'service_history_request', serviceHistoryCodes, allStatusSelected, t('forms:status.label')),
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'service_history_request'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
      },
      {
        Header: t('forms:submitted_at.label', { date: '' }),
        accessor: (row: { submitted_at: string }) => dateTimeTransformer(row.submitted_at) ?? '-',
      },
      {
        Header: t('workshop:language.label'),
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: language,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownLanguages,
              translationKey: 'location:languages',
            }),
            onChange: setLanguage,
          }),
        accessor: (row: { workshop: Workshop }) => t(`location:languages.${row.workshop?.language.toUpperCase()}`),
      },
      {
        Header: t('forms:vin.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.vin,
      },
      {
        Header: t('forms:workshop.label'),
        accessor: (row: { workshop: Workshop }) => <Link to={`/app/op/workshops/${row.workshop?.id}`}>{row.workshop?.user_readable_id}</Link>,
      },
      {
        Header: t('forms:make.label'),
        accessor: (row: { vehicle: Vehicle }) => row.vehicle?.manufacturer?.make?.name,
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: make,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: makes,
              labelColumn: 'name',
              valueColumn: 'id',
            }),
            onChange: setMake,
          }),
      },
      {
        Header: t('workshop:system_partner.label'),
        accessor: 'systempartner_id',
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: systemPartner,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: systemPartners as any,
              valueColumn: 'id',
              labelColumn: 'name',
            }),
            onChange: setSystemPartner,
          }),
        // @ts-ignore
      },
      {
        Header: t('workshop:country.label'),
        accessor: (row: { workshop: Workshop }) => t(`location:countries.${row?.workshop?.country}`),
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownCountries,
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      serviceHistoryCodes,
      initialStatus,
      allStatusSelected,
      searchString,
      status,
      assignee,
      itemsPerPage,
      page,
      language,
      systemPartner,
      systemPartners,
    ],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('service_history_request:header')}
      data-test-id="service-history-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setAssignee(initialFilters?.assignee);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setMake(initialFilters?.make);
              setLanguage(initialFilters?.language);
              setSystemPartner(initialFilters?.systemPartner);
              setCountry(initialFilters?.country);
              window.localStorage.setItem('service_history_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="service-history-table"
            data={serviceHistoryQuery.data?.data || []}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('service_history_request:search')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={serviceHistoryQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={serviceHistoryQuery.isFetching || serviceHistoryQuery.isLoading}
            isMoreThenOnePage={
              // @ts-ignore
              !initialLoad || Boolean(serviceHistoryQuery.data?.links?.next) || currentPage > 0
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default ServiceHistoryOverviewCard;
